import type { CSSProperties, ReactNode } from "react";

// Brand parts for the marketing site, ported from the app (ChamferBox, the comparison line and
// headline, gapTone). Keep them in step with app/src/components/ui.

export const NAVY = "#003e56";

const octagon = (cut: number) =>
  `polygon(${cut}px 0, calc(100% - ${cut}px) 0, 100% ${cut}px, 100% calc(100% - ${cut}px), calc(100% - ${cut}px) 100%, ${cut}px 100%, 0 calc(100% - ${cut}px), 0 ${cut}px)`;
const octOuter = octagon(6.4);
const octInner = octagon(6);
const bodyTop = "polygon(4px 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 0 100%, 0 4px)";

/** A chamfered panel: a Mist head over a white body, or the body alone. */
export function ChamferBox({ head, children, shadow = false, className = "", style }: {
  head?: ReactNode;
  children: ReactNode;
  shadow?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={className} style={{ ...style, ...(shadow ? { filter: "drop-shadow(0 2px 6px rgba(15,23,42,0.10))" } : null) }}>
      <div className="p-[1px] bg-slate-300/70" style={{ clipPath: octOuter }}>
        <div className={`flex flex-col ${head ? "bg-logo-background" : "bg-white"}`} style={{ clipPath: octInner }}>
          {head}
          <div className="bg-white" style={head ? { clipPath: bodyTop } : undefined}>{children}</div>
        </div>
      </div>
    </div>
  );
}

/** Ours against the market: cool when ours is lower, gold when higher, grey when level. */
export function gapTone(pct: number, soft = 5, strong = 15) {
  if (pct <= -strong) return "rgba(63,127,154,0.16)";
  if (pct <= -soft) return "rgba(63,127,154,0.08)";
  if (pct < soft) return "#f1f5f9";
  if (pct < strong) return "rgba(201,150,43,0.10)";
  return "rgba(201,150,43,0.20)";
}

/** A panel's answer in one line: a large figure on its gap tint, what it means, the numbers behind it. */
export function GapHeadline({ value, label, tint, children }: { value: string; label: string; tint?: number; children?: ReactNode }) {
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-1.5">
        <span
          className={`whitespace-nowrap text-[30px] font-semibold leading-tight tabular-nums text-slate-900 ${tint != null ? "-ml-1.5 px-1.5" : ""}`}
          style={tint != null ? { backgroundColor: gapTone(tint) } : undefined}
        >
          {value}
        </span>
        <span className="whitespace-nowrap text-[15px] font-medium text-slate-700">{label}</span>
      </div>
      {children && <div className="text-[13px] tabular-nums text-slate-500">{children}</div>}
    </>
  );
}

export function Figure({ ours = false, children }: { ours?: boolean; children: ReactNode }) {
  return <span className={`font-medium ${ours ? "text-logo" : "text-slate-900"}`}>{children}</span>;
}

/** Where ours sits against the others: their range as a band, the median as a tick, ours as a diamond. */
export function RangeLine({ low, high, median, ours, domain, labels = false, fill = false }: {
  low?: number;
  high?: number;
  median?: number;
  ours: number;
  domain: [number, number];
  labels?: boolean;
  fill?: boolean;
}) {
  const at = (v: number) => `${Math.min(1, Math.max(0, (v - domain[0]) / (domain[1] - domain[0]))) * 100}%`;
  const band = low != null && high != null;
  return (
    <div>
      <div className="relative h-[13px]">
        <div className="absolute inset-x-0 top-[5px] h-[3px]" style={{ backgroundColor: band ? "#f1f5f9" : "#e2e8f0" }} />
        {band && <div className="absolute top-[5px] h-[3px] bg-slate-300" style={{ left: at(low), width: `calc(${at(high)} - ${at(low)})` }} />}
        {fill && <div className="absolute left-0 top-[5px] h-[3px] bg-logo/35" style={{ width: at(ours) }} />}
        {median != null && <div className="absolute top-0 -ml-px h-[13px] w-[2px] bg-slate-600" style={{ left: at(median) }} />}
        <div className="absolute top-[1px] -ml-[5px] h-[11px] w-[11px] rotate-45 border-2 border-white bg-logo" style={{ left: at(ours) }} />
      </div>
      {labels && band && (
        <div className="relative mt-0.5 h-3 text-[10px] tabular-nums text-slate-700">
          <span className="absolute -translate-x-1/2" style={{ left: at(low) }}>${low.toFixed(2)}</span>
          <span className="absolute -translate-x-1/2" style={{ left: at(high) }}>${high.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
}
